import { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { t, countryName } from '@/i18n';
import { formatBps, formatKm, formatMinor, formatNumber, isoDate } from '@/lib/format';
import { useReportSummary } from '@/api/hooks/reports';
import { Card, CardBody, CardHeader, Stat } from '@/components/ui/Card';
import { DatePicker, Select } from '@/components/ui/Field';
import {
  CustomersReportView,
  DriversReportView,
  TripsReportView,
  VansReportView,
} from '@/components/reports/OperationsReports';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import {
  CardList,
  CardListEmpty,
  CardListItem,
  DataRow,
  NarrowOnly,
  WideOnly,
} from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { BarChart, Sparkline } from '@/components/charts/Sparkline';

type PeriodKey = 'thisMonth' | 'lastMonth' | 'thisYear' | 'last12';

function periodRange(key: PeriodKey, now = new Date()): { from: string; to: string } {
  const year = now.getFullYear();
  const month = now.getMonth();
  switch (key) {
    case 'thisMonth':
      return { from: isoDate(new Date(year, month, 1)), to: isoDate(new Date(year, month + 1, 0)) };
    case 'lastMonth':
      return { from: isoDate(new Date(year, month - 1, 1)), to: isoDate(new Date(year, month, 0)) };
    case 'thisYear':
      return { from: isoDate(new Date(year, 0, 1)), to: isoDate(new Date(year, 11, 31)) };
    case 'last12':
    default:
      return {
        from: isoDate(new Date(year, month - 11, 1)),
        to: isoDate(new Date(year, month + 1, 0)),
      };
  }
}

export type ReportTab = 'summary' | 'trips' | 'customers' | 'drivers' | 'vans';
export const REPORT_TABS: Array<[ReportTab, string]> = [
  ['summary', 'Общо'],
  ['trips', 'Курсове'],
  ['customers', 'Клиенти'],
  ['drivers', 'Шофьори'],
  ['vans', 'Бусове'],
];

/**
 * СПРАВКИТЕ. Клиентът: „справки по курсове, клиенти и всякакви полезни
 * справки". Общото е числата на управителя; останалите раздели са таблици ред
 * по ред, които се свалят за Excel. Периодът е общ за всички раздели.
 */
export function ReportsPage() {
  const search = useSearch({ from: '/reports' });
  const navigate = useNavigate({ from: '/reports' });
  const tab: ReportTab = search.tab ?? 'summary';
  const [period, setPeriod] = useState<PeriodKey | 'custom'>('last12');
  const [custom, setCustom] = useState(() => periodRange('thisMonth'));
  const { from, to } = useMemo(() => (period === 'custom' ? custom : periodRange(period)), [period, custom]);

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex flex-wrap items-center gap-3 px-3 pt-3 sm:px-4 sm:pt-4">
        <h1 className="font-display text-lg font-bold tracking-tight">{t.reports.title}</h1>
        <Select
          className="w-full sm:w-52"
          aria-label={t.reports.period}
          value={period}
          onChange={(e) => setPeriod(e.target.value as PeriodKey | 'custom')}
          options={[
            { value: 'thisMonth', label: t.reports.thisMonth },
            { value: 'lastMonth', label: t.reports.lastMonth },
            { value: 'thisYear', label: t.reports.thisYear },
            { value: 'last12', label: t.reports.last12 },
            { value: 'custom', label: 'Избрани дати' },
          ]}
        />
        {period === 'custom' ? (
          <span className="flex items-center gap-1.5 text-xs">
            <DatePicker
              withTime={false}
              aria-label="От"
              className="h-9 w-40"
              value={custom.from}
              onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })}
            />
            —
            <DatePicker
              withTime={false}
              aria-label="До"
              className="h-9 w-40"
              value={custom.to}
              onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })}
            />
          </span>
        ) : (
          <span className="text-xs text-ink-faint">
            {from} → {to}
          </span>
        )}
      </div>

      <div className="mt-2 flex gap-1 overflow-x-auto border-b border-line px-3 sm:px-4">
        {REPORT_TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => void navigate({ search: (s) => ({ ...s, tab: key === 'summary' ? undefined : key }) })}
            className={cn(
              '-mb-px shrink-0 rounded-t-lg border border-b-0 px-4 py-2 text-sm',
              tab === key ? 'border-line bg-surface font-medium text-accent' : 'border-transparent text-ink-dim hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 p-3 sm:gap-4 sm:p-4">
        {from > to ? (
          <p className="text-sm text-warn">Началото на периода е след края му.</p>
        ) : tab === 'trips' ? (
          <TripsReportView from={from} to={to} />
        ) : tab === 'customers' ? (
          <CustomersReportView from={from} to={to} />
        ) : tab === 'drivers' ? (
          <DriversReportView from={from} to={to} />
        ) : tab === 'vans' ? (
          <VansReportView from={from} to={to} />
        ) : (
          <SummaryReport from={from} to={to} />
        )}
      </div>
    </div>
  );
}

/**
 * Числата на управителя. Всяка справка показва празния пробег до прихода —
 * приход на курс без празните километри е безполезно число.
 */
function SummaryReport({ from, to }: { from: string; to: string }) {
  const { data, isLoading, isError, refetch } = useReportSummary(from, to);

  const series = data?.series ?? [];
  const revenueSeries = series.map((point) => point.revenueMinor / 100);
  const marginSeries = series.map((point) => point.marginMinor / 100);
  const emptyShareSeries = series.map((point) =>
    point.km > 0 ? Math.round((point.emptyKm / point.km) * 100) : 0,
  );

  return (
    <>
      {isLoading ? (
        <LoadingBlock />
      ) : isError || !data ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : (
        <>
          <Card>
            <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6 lg:grid-cols-7">
              <Stat
                label={t.reports.revenue}
                value={formatMinor(data.revenueMinor, data.ccy, { compact: true })}
                hint={<Sparkline values={revenueSeries} label={`${t.reports.revenue} по месеци`} />}
              />
              <Stat
                label={t.reports.cost}
                value={formatMinor(data.costMinor, data.ccy, { compact: true })}
              />
              <Stat
                label={t.reports.margin}
                value={formatMinor(data.marginMinor, data.ccy, { compact: true })}
                hint={<Sparkline values={marginSeries} label={`${t.reports.margin} по месеци`} />}
                tone={data.marginMinor < 0 ? 'danger' : 'ok'}
              />
              <Stat label={t.reports.marginPct} value={formatBps(data.marginBps)} />
              <Stat label={t.reports.orders} value={formatNumber(data.orders)} />
              <Stat label={t.reports.km} value={formatKm(data.km)} />
              <Stat
                label={t.reports.emptyKmShare}
                value={formatBps(data.emptyKmBps)}
                hint={
                  <Sparkline
                    values={emptyShareSeries}
                    label="Дял празен пробег по месеци"
                    className={data.emptyKmBps > 3000 ? 'text-warn' : 'text-accent'}
                  />
                }
                tone={data.emptyKmBps > 3000 ? 'warn' : 'default'}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t.reports.byMonth}
              subtitle="Сивото е приходът, зеленото — маржът, който остава след разходите."
            />
            <CardBody>
              <BarChart
                label={`${t.reports.revenue} и ${t.reports.margin} по месеци`}
                data={series.map((point) => ({
                  label: point.label,
                  value: point.revenueMinor / 100,
                  secondary: Math.max(point.marginMinor / 100, 0),
                }))}
                formatValue={(value) =>
                  formatMinor(Math.round(value * 100), data.ccy, { compact: true })
                }
              />
            </CardBody>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="overflow-hidden">
              <CardHeader title={t.reports.topPartners} />
              <NarrowOnly>
                {data.topPartners.length === 0 ? (
                  <CardListEmpty>{t.common.empty}</CardListEmpty>
                ) : (
                  <CardList>
                    {data.topPartners.map((row) => (
                      <CardListItem key={row.partnerId}>
                        <div className="flex items-baseline gap-2">
                          <span className="min-w-0 flex-1 truncate font-medium">
                            {row.partnerName}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 font-mono text-sm tabular',
                              row.marginMinor < 0 && 'text-danger',
                            )}
                          >
                            {formatMinor(row.marginMinor, data.ccy, { compact: true })}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                          <span>
                            {t.reports.orders}: {row.orders}
                          </span>
                          <span>
                            {t.reports.revenue}:{' '}
                            {formatMinor(row.revenueMinor, data.ccy, { compact: true })}
                          </span>
                          <span>{formatBps(row.marginBps)}</span>
                        </div>
                      </CardListItem>
                    ))}
                  </CardList>
                )}
              </NarrowOnly>
              <WideOnly>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t.partners.name}</TH>
                      <TH numeric>{t.reports.orders}</TH>
                      <TH numeric>{t.reports.revenue}</TH>
                      <TH numeric>{t.reports.margin}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {data.topPartners.length === 0 ? (
                      <TableEmpty colSpan={4}>{t.common.empty}</TableEmpty>
                    ) : (
                      data.topPartners.map((row) => (
                        <TR key={row.partnerId}>
                          <TD className="max-w-[16rem] truncate">{row.partnerName}</TD>
                          <TD numeric>{row.orders}</TD>
                          <TD numeric>
                            {formatMinor(row.revenueMinor, data.ccy, { compact: true })}
                          </TD>
                          <TD numeric className={row.marginMinor < 0 ? 'text-danger' : undefined}>
                            {formatMinor(row.marginMinor, data.ccy, { compact: true })}
                            <div className="text-[11px] text-ink-faint">
                              {formatBps(row.marginBps)}
                            </div>
                          </TD>
                        </TR>
                      ))
                    )}
                  </TBody>
                </Table>
              </WideOnly>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader title={t.reports.lanes} />
              <NarrowOnly>
                {data.lanes.length === 0 ? (
                  <CardListEmpty>{t.common.empty}</CardListEmpty>
                ) : (
                  <CardList>
                    {data.lanes.map((lane) => (
                      <CardListItem key={`${lane.fromCountry}-${lane.toCountry}`}>
                        <div className="flex items-baseline gap-2">
                          <span className="min-w-0 flex-1 truncate">
                            {countryName(lane.fromCountry)} → {countryName(lane.toCountry)}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 font-mono text-sm tabular',
                              lane.marginMinor < 0 && 'text-danger',
                            )}
                          >
                            {formatMinor(lane.marginMinor, data.ccy, { compact: true })}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                          <span>
                            {t.reports.orders}: {lane.orders}
                          </span>
                          <span>{formatKm(lane.km)}</span>
                        </div>
                      </CardListItem>
                    ))}
                  </CardList>
                )}
              </NarrowOnly>
              <WideOnly>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t.orders.route}</TH>
                      <TH numeric>{t.reports.orders}</TH>
                      <TH numeric>{t.reports.km}</TH>
                      <TH numeric>{t.reports.margin}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {data.lanes.length === 0 ? (
                      <TableEmpty colSpan={4}>{t.common.empty}</TableEmpty>
                    ) : (
                      data.lanes.map((lane) => (
                        <TR key={`${lane.fromCountry}-${lane.toCountry}`}>
                          <TD className="whitespace-nowrap">
                            {countryName(lane.fromCountry)} → {countryName(lane.toCountry)}
                          </TD>
                          <TD numeric>{lane.orders}</TD>
                          <TD numeric>{formatKm(lane.km)}</TD>
                          <TD numeric className={lane.marginMinor < 0 ? 'text-danger' : undefined}>
                            {formatMinor(lane.marginMinor, data.ccy, { compact: true })}
                          </TD>
                        </TR>
                      ))
                    )}
                  </TBody>
                </Table>
              </WideOnly>
            </Card>
          </div>

          <Card className="overflow-hidden">
            <CardHeader
              title={t.reports.fleetUse}
              subtitle="Натовареност = дни с курс от дните в периода."
            />
            <NarrowOnly>
              {data.vehicles.length === 0 ? (
                <CardListEmpty>{t.common.empty}</CardListEmpty>
              ) : (
                <CardList>
                  {data.vehicles.map((vehicle) => {
                    const emptyShare = vehicle.km > 0 ? vehicle.emptyKm / vehicle.km : 0;
                    return (
                      <CardListItem key={vehicle.vehicleId}>
                        <div className="flex items-baseline gap-2">
                          <span className="font-display text-base font-semibold">
                            {vehicle.fleetNo}
                          </span>
                          <span className="font-mono text-xs text-ink-faint">{vehicle.plate}</span>
                          <span
                            className={cn(
                              'ml-auto shrink-0 font-mono text-sm tabular',
                              vehicle.marginMinor < 0 && 'text-danger',
                            )}
                          >
                            {formatMinor(vehicle.marginMinor, data.ccy, { compact: true })}
                          </span>
                        </div>
                        <dl className="mt-1">
                          <DataRow label={t.reports.trips}>{vehicle.trips}</DataRow>
                          <DataRow label={t.reports.km}>{formatKm(vehicle.km)}</DataRow>
                          <DataRow label={t.trips.emptyKm}>
                            <span className={emptyShare > 0.3 ? 'text-warn' : undefined}>
                              {formatKm(vehicle.emptyKm)} ({Math.round(emptyShare * 100)} %)
                            </span>
                          </DataRow>
                          <DataRow label={t.reports.revenue}>
                            {formatMinor(vehicle.revenueMinor, data.ccy, { compact: true })}
                          </DataRow>
                          <DataRow label="Натовареност">
                            {formatBps(vehicle.utilisationBps)}
                          </DataRow>
                        </dl>
                      </CardListItem>
                    );
                  })}
                </CardList>
              )}
            </NarrowOnly>
            <WideOnly>
              <Table>
                <THead>
                  <TR>
                    <TH>{t.vehicles.fleetNo}</TH>
                    <TH>{t.vehicles.plate}</TH>
                    <TH numeric>{t.reports.trips}</TH>
                    <TH numeric>{t.reports.km}</TH>
                    <TH numeric>{t.trips.emptyKm}</TH>
                    <TH numeric>{t.reports.revenue}</TH>
                    <TH numeric>{t.reports.margin}</TH>
                    <TH numeric>Натовареност</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.vehicles.length === 0 ? (
                    <TableEmpty colSpan={8}>{t.common.empty}</TableEmpty>
                  ) : (
                    data.vehicles.map((vehicle) => {
                      const emptyShare = vehicle.km > 0 ? vehicle.emptyKm / vehicle.km : 0;
                      return (
                        <TR key={vehicle.vehicleId}>
                          <TD className="font-display font-semibold">{vehicle.fleetNo}</TD>
                          <TD className="font-mono text-xs">{vehicle.plate}</TD>
                          <TD numeric>{vehicle.trips}</TD>
                          <TD numeric>{formatKm(vehicle.km)}</TD>
                          <TD numeric className={emptyShare > 0.3 ? 'text-warn' : undefined}>
                            {formatKm(vehicle.emptyKm)}
                            <div className="text-[11px] text-ink-faint">
                              {Math.round(emptyShare * 100)} %
                            </div>
                          </TD>
                          <TD numeric>
                            {formatMinor(vehicle.revenueMinor, data.ccy, { compact: true })}
                          </TD>
                          <TD
                            numeric
                            className={vehicle.marginMinor < 0 ? 'text-danger' : undefined}
                          >
                            {formatMinor(vehicle.marginMinor, data.ccy, { compact: true })}
                          </TD>
                          <TD numeric>{formatBps(vehicle.utilisationBps)}</TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </WideOnly>
          </Card>
        </>
      )}
    </>
  );
}
